// `extension-iframe-utils-listener.service.ng1.ts`'s `UserAuthStateChange` arm, plus the
// `onAuthStateChange` it delegates to.
//
// ⚠️ INSTALLED ON THE ROUTER, NOT INSIDE A COMPONENT, because there is no component that is
// always mounted. The route tree has five top-level branches with five different elements —
// `AuthLayout`, `ExtensionLayout`, `FullPageDialogLayout`, the standalone routes and the
// notifications frame — so a hook would have to be repeated in each, and the one that got
// forgotten would be a surface that silently stops following the session. Upstream has the
// same shape and solves it the same way: one injected singleton, not a directive.
import type { DataRouter } from 'react-router-dom'
import type { IUserAuthStateChangeParams } from '@woo-ext/protocol'
import { nextPathFor, resolveSessionState } from '@woo-ext/ui'
import { subscribeToAuthStateChange } from './authBroadcast.ts'

/** `chrome-extension.auth.sso.callback`, the one state upstream names explicitly. */
const SSO_CALLBACK = '/chrome-extension/auth/sso/callback'
/** `FrameRole.Notifications` — the shell's second, hidden iframe. */
const NOTIFICATIONS = '/chrome-extension/notifications'

/**
 * Whether this surface should NAVIGATE on an auth change.
 *
 * ⚠️ THE GUARD IS ON THE NAVIGATION ALONE, NOT ON THE WHOLE HANDLER, and getting that
 * backwards is the easy mistake — upstream's early return sits BELOW
 * `updateSessionAndCareeroGlobal()` and the event emit. A notifications frame that stopped
 * re-reading the session would keep polling as the signed-out user, and the SSO callback
 * would finish its handshake against a session it had thrown away.
 */
export function shouldNavigate(pathname: string, frameRole: string | null): boolean {
  if (frameRole === 'notifications') return false
  if (pathname === NOTIFICATIONS) return false
  return pathname !== SSO_CALLBACK
}

/** Just enough of the data router to drive this without one. */
export interface AuthStateRouter {
  revalidate: () => void | Promise<void>
  navigate: (to: string, options?: { replace?: boolean; state?: unknown }) => void | Promise<void>
}

/**
 * `onAuthStateChange` — what a surface does when some OTHER surface changed the session.
 *
 * Exported separately from the subscription so the decisions below are reachable without a
 * router, a DOM or a channel.
 */
export async function handleAuthStateChange(
  router: AuthStateRouter,
  location: { pathname: string; search: string },
  params: IUserAuthStateChangeParams,
): Promise<void> {
  // `updateSessionAndCareeroGlobal()`. The bootstrap is a loader here rather than a window
  // global, so re-reading it is a revalidation — which re-runs every loader on the matched
  // tree, not just the root's, and is why the screens below the layout pick up the new
  // session without being told about it individually.
  //
  // ⚠️ IT HAPPENS BEFORE THE GUARD BELOW, NOT AFTER. Upstream's early return sits under
  // `updateSessionAndCareeroGlobal()` and the event emit, and the ordering is the rule: a
  // notifications frame that stopped re-reading would keep polling as the signed-out user,
  // and the SSO callback would finish its handshake against a session it had thrown away.
  await router.revalidate()

  if (!shouldNavigate(location.pathname, new URLSearchParams(location.search).get('frameRole'))) return

  // `getNextStateBasedOnSession()` + `$state.go(state, params.data)`.
  //
  // ⚠️ THIS LANDS ON `/auth/status`, WHERE UPSTREAM LANDS ON THE SHORTLIST. `nextPathFor` is
  // the port's whole post-login decision today — `otherwiseRedirectTo` in routes/auth sends a
  // local sign-in to exactly the same place — so the broadcast deliberately does not know
  // better than the path beside it. Teaching both to resolve upstream's `getPostLoginState`
  // (the ClientContact/Candidates group split, `isInIframe`, `allowFrameLessShortlist`) is
  // one change to make in one place, not two.
  const to = nextPathFor(await resolveSessionState())
  // `replace`, because this is not a place the user navigated to, and a Back that returns
  // them to a screen the session no longer permits is a loop against the guard.
  await router.navigate(to, { replace: true, ...(params.data === undefined ? {} : { state: params.data }) })
}

/**
 * Subscribe this page to auth changes raised by any other surface. Returns the unsubscribe.
 */
export function installAuthStateListener(router: DataRouter): () => void {
  return subscribeToAuthStateChange((params) => {
    handleAuthStateChange(router, window.location, params).catch(() => {
      // Best-effort, as upstream's call sites all are: a failed revalidation leaves the
      // surface showing what it showed before, which is strictly better than tearing it down.
    })
  })
}
